import React, { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";
import {
  FaPaperPlane, FaGithub, FaLinkedin, FaInstagram,
  FaEnvelope, FaPhone, FaMapMarkerAlt,
} from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import { personalInfo, socialLinks } from "../../data/portfolioData";
import { useLanguage } from "../../context/LanguageContext";
import "./Contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const { t } = useLanguage();
  const whatsappUrl = `${socialLinks.whatsapp}?text=${encodeURIComponent(t.contact.whatsappMessage)}`;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setError(false);

    emailjs
      .sendForm(
        "service_fgj1cnr",   // TODO: Replace with your EmailJS Service ID
        "template_vxp4nti",  // TODO: Replace with your EmailJS Template ID
        formRef.current,
        "58AF8LDyY_FCm5lnL"    // TODO: Replace with your EmailJS Public Key
      )
      .then(() => {
        setSubmitted(true);
        setFormData({ name: "", email: "", message: "" });
        setSending(false);
        setTimeout(() => setSubmitted(false), 4000);
      })
      .catch(() => {
        setError(true);
        setSending(false);
        setTimeout(() => setError(false), 4000);
      });
  };

  return (
    <section className="contact" id="contact" ref={sectionRef}>
      <div className="contact__container">
        <h2 className="section-title">{t.contact.title}</h2>
        <p className="section-subtitle">{t.contact.subtitle}</p>

        <div className="contact__grid">
          {/* Info */}
          <div className={`contact__info ${visible ? "animate-slide-right" : ""}`}>
            <h3>{t.contact.connect}</h3>
            <p>{t.contact.text}</p>

            <div className="contact__details">
              <div className="contact__detail">
                <div className="contact__detail-icon">
                  <FaEnvelope />
                </div>
                <div>
                  <span>{t.contact.email}</span>
                  <a href={`mailto:${personalInfo.email}`}>
                    {personalInfo.email}
                  </a>
                </div>
              </div>
              <div className="contact__detail">
                <div className="contact__detail-icon">
                  <FaPhone />
                </div>
                <div>
                  <span>{t.contact.phone}</span>
                  <a href={`tel:${personalInfo.phone.replace(/[^\d+]/g, "")}`}>
                    {personalInfo.phone}
                  </a>
                </div>
              </div>
              <div className="contact__detail">
                <div className="contact__detail-icon">
                  <FaMapMarkerAlt />
                </div>
                <div>
                  <span>{t.contact.location}</span>
                  <p>{personalInfo.location}</p>
                </div>
              </div>
            </div>

            <div className="contact__socials">
              <a href={socialLinks.github} target="_blank" rel="noreferrer" aria-label="GitHub">
                <FaGithub />
              </a>
              <a href={socialLinks.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                <FaLinkedin />
              </a>
              <a href={socialLinks.gmail} aria-label={`Email ${personalInfo.email}`}>
                <FaEnvelope />
              </a>
              <a href={socialLinks.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
                <FaInstagram />
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={t.contact.whatsapp}
                className="contact__social--whatsapp"
              >
                <FaWhatsapp />
              </a>
            </div>
          </div>

          {/* Form */}
          <form
            ref={formRef}
            className={`contact__form ${visible ? "animate-slide-left" : ""}`}
            onSubmit={handleSubmit}
          >
            {submitted && (
              <div className="contact__success">
                {t.contact.success}
              </div>
            )}
            {error && (
              <div className="contact__error">
                {t.contact.error}
              </div>
            )}

            <div className="form__group">
              <label htmlFor="name">{t.contact.name}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="form__group">
              <label htmlFor="email">{t.contact.emailAddress}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
              />
            </div>

            <div className="form__group">
              <label htmlFor="message">{t.contact.message}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t.contact.messagePlaceholder}
                rows="5"
                required
              />
            </div>

            <button type="submit" className="btn btn--primary btn--full" disabled={sending}>
              <FaPaperPlane /> {sending ? t.contact.sending : t.contact.send}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
